import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import notifications from "../../../../common/Components/Notifications/Notification";
import { isKarkhanaSupervisor } from "../../../../helpers/karkhanaEnabled";
import { listCashHolders, printPaySheet } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { confirmDialog } from "../../ui/dialog";
import { readRole } from "../CashbookGuard";
import { refusalText } from "../money";
import type { Holder, PrintView } from "../types";
import "../cashbook.scss";

/** Prints made in this tab just now print without the "कॉपी · Copy" mark; anything opened later carries it. */
const fresh = new Set<string>();
export const markFreshPrint = (printId: string) => fresh.add(printId);
/** True once for a print just made here (a reload or a later visit is a copy). */
export const takeFreshPrint = (printId: string): boolean => fresh.delete(printId);

/**
 * "Print" on a pay sheet with the Cash book add-on (V1_SPEC 7.1, 11.4 E3): freeze a numbered print on
 * the server, then open it to print. Supervisors hold their own prints; owners and managers say who pays
 * from it (a supervisor, or the office). A live print asks before printing again (the new one replaces it).
 */
export const useCashPrint = () => {
  const { t } = useKT();
  const history = useHistory();
  const supervisor = isKarkhanaSupervisor(readRole());
  const [picking, setPicking] = useState<string | null>(null);
  const [holders, setHolders] = useState<Holder[] | null>(null);
  const [busy, setBusy] = useState(false);

  const doPrint = async (paySheetId: string, holderUserId: string | null, reprint = false): Promise<void> => {
    setBusy(true);
    try {
      const res = await printPaySheet(paySheetId, { reprint, holderUserId });
      if (res?.success && res.data?.printId) {
        markFreshPrint(res.data.printId);
        history.push(kp.cashPrint(res.data.printId));
        return;
      }
      if (res?.code === "PRINT_EXISTS") {
        const live = res.data as unknown as PrintView | null;
        const again = await confirmDialog({
          title: t("Printed already (Print {{n}}). Print again? The new print replaces the old one.", { n: live?.revision ?? 1 }),
          ok: t("Print again"),
        });
        // The choice stands on a reprint: the office (null) stays the office, never the old print's supervisor.
        if (again) await doPrint(paySheetId, holderUserId, true);
        return;
      }
      notifications({ title: t("Not printed"), message: refusalText(res, t), type: "warning" });
    } catch {
      notifications({ title: t("No connection"), message: t("No network. Nothing changed; tap again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const start = (paySheetId: string) => {
    if (supervisor) {
      doPrint(paySheetId, null);
      return;
    }
    setPicking(paySheetId);
    if (!holders) {
      listCashHolders()
        .then((res) => setHolders(res?.success ? res.data?.holders || [] : []))
        .catch(() => setHolders([]));
    }
  };

  const choose = (holderUserId: string | null) => {
    const id = picking;
    setPicking(null);
    if (id) doPrint(id, holderUserId);
  };

  const element = (
    <Modal isOpen={!!picking} toggle={() => setPicking(null)} centered>
      <ModalHeader toggle={() => setPicking(null)}>{t("Who pays from this sheet?")}</ModalHeader>
      <ModalBody>
        <p className="cb-muted">{t("The supervisor who pays sends the photo in the evening. Cash paid from it comes off their cash in hand.")}</p>
        {holders === null ? <div className="cb-skeleton" /> : null}
        <div className="cb-list">
          {(holders || []).map((h) => (
            <button key={h.userId} type="button" className="cb-item" onClick={() => choose(h.userId)}>
              <span className="cb-item__main">{h.name}</span>
              <span aria-hidden="true">›</span>
            </button>
          ))}
          <button type="button" className="cb-item" onClick={() => choose(null)}>
            <span className="cb-item__main">
              {t("Office (I pay)")}
              <span className="cb-item__sub">{t("Not from a supervisor's cash")}</span>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        </div>
      </ModalBody>
    </Modal>
  );

  return { start, busy, element };
};
